import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Hotel, Ota } from 'frontend-utils/models';
import { PLATFORMS } from 'frontend-utils/utils/constants/constant-values';
import type { PeriodParity } from 'lumen/models/period';
import type HotelParityPosRoute from 'lumen/routes/hotel/parity/pos';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import formatDate from 'lumen/utils/format-local-date';
import type { ModelFrom } from 'lumen/utils/routes';

export default class HotelParityPosController extends Controller {
  declare model: ModelFrom<HotelParityPosRoute>;

  // @ts-expect-error: Property 'queryParams' in type 'PosController' is not assignable to the same property in base type 'Controller<unknown>'.
  queryParams = [{ maxPersons: { scope: 'controller' } }];

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare ota: Services['ota'];
  @service declare rates: Services['rates'];
  @service declare simpleExport: Services['simple-export'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked maxPersons = 0;

  get availableOTAs(): Ota[] {
    const otas = [...this.hotel.getParityOTAs(PLATFORMS.PLATFORM_DESKTOP)];

    return otas.sort((a, b) => a.order - b.order);
  }

  get hotel(): Hotel {
    return this.hotelAndMarketService.hotel;
  }

  get hotelId(): string | null {
    return this.hotel.id;
  }

  get isPosAvailable(): boolean | undefined {
    return this.hotel.OTAsParity?.find((ota) => ota.id === 'trivago')
      ?.isConnected;
  }

  get los(): number | undefined {
    return this.hotel.defaultLosParity[0];
  }

  @action
  handleExport(): void {
    if (!this.model?.isLoading) {
      this.usageAnalytics.track('Data Exported', {
        eventTrigger: 'parity-pos-view',
      });

      const allowedOtaIds = this.availableOTAs.map((ota) => ota.id);
      const headers = [
        this.intl.t('general.arrival_date').toString(),
        this.intl.t('parity.pos.pos').toString(),
        this.intl.t('parity.pos.hotel_rate').toString(),
        this.intl.t('general.major_otas').toString(),
        this.intl.t('rates.lowest_rate').toString(),
        this.intl.t('general.other_otas').toString(),
        this.intl.t('rates.lowest_rate').toString(),
      ];
      const title = this.intl.t('parity.pos_view').toString();
      const sheetName = title;

      const content = this.model?.periods.map((period) => {
        const paritiesPOSDict = period.paritiesPOSDict!;
        const date = formatDate(period.day?.date);
        const { pos } = period;

        const majorOtas = Object.keys(paritiesPOSDict).filter(
          (key) =>
            key !== 'others' &&
            allowedOtaIds.indexOf(paritiesPOSDict[key]!.ota) !== -1,
        );

        const baseRates: PeriodParity[] = [];
        const majorParitiesWithIssues: PeriodParity[] = [];
        const otherParitiesWithIssues = paritiesPOSDict.others.filter(
          (parity) =>
            allowedOtaIds.indexOf(parity.ota) !== -1 &&
            parity.position_to_baserate < 0,
        );

        majorOtas.forEach((ota) => {
          const parity = paritiesPOSDict[ota]!;

          if (parity.is_baserate) {
            baseRates.push(parity);
          }

          if (parity.position_to_baserate < 0) {
            majorParitiesWithIssues.push(parity);
          }
        });

        const [baseRate] = baseRates;
        const [lowestMajorParity] = majorParitiesWithIssues;
        const [lowestOtherParity] = otherParitiesWithIssues;

        const hotelRate = baseRate
          ? `${this.rates.formatRateObject(baseRate, false)} ${baseRate.currency}`
          : this.intl.t('general.not_available_short');

        let lowestMajorParityIssue = this.intl.t('general.missing');
        let formattedMajorIssues = this.intl.t('parity.no_issues');
        let lowestOtherParityIssue = this.intl.t('general.missing');
        let formattedOtherIssues = this.intl.t('parity.no_issues');

        if (lowestMajorParity) {
          lowestMajorParityIssue = `${this.rates.formatRateObject(
            lowestMajorParity,
            false,
          )} ${lowestMajorParity.currency}`;
          formattedMajorIssues = majorParitiesWithIssues
            .map((parity) => this.ota.otaIdToPrettyName(parity.channel))
            .join(', ');
        }

        if (lowestOtherParity) {
          lowestOtherParityIssue = `${this.rates.formatRateObject(
            lowestOtherParity,
            false,
          )} ${lowestOtherParity.currency}`;
          formattedOtherIssues = otherParitiesWithIssues
            .map((parity) => this.ota.otaIdToPrettyName(parity.channel))
            .join(', ');
        }

        return [
          date.toString(),
          pos,
          hotelRate.toString(),
          formattedMajorIssues,
          lowestMajorParityIssue.toString(),
          formattedOtherIssues,
          lowestOtherParityIssue.toString(),
        ];
      });

      const postData = JSON.stringify({
        headers,
        content,
        title,
        sheetName,
      });

      void this.simpleExport.execute(postData, this.hotelId!);
    }
  }
}
